import { useCallback, useEffect, useMemo, useState } from 'react';

import TableInput from '@components/table-input/TableInput';
import {
  TableId,
  TableInputCellValidation,
  TableInputOnChange,
  TableInputRowData,
  TableInputSelectOption,
} from '@components/table-input/tableInput.types';
import {
  AquaticResourceData,
  AR_PROP_COL_IDX,
  arTableInputColumnData,
  buildAquaticResourceRow,
  convertArApiDataToTableInputData,
  getResolvedDuplicateWaterNameIds,
  getResolvedDuplicateWaterNameIdsAfterUpdate,
  getWaterNameCounts,
  MEASUREMENT_UNIT_AREA_OPTIONS,
  MEASUREMENT_UNIT_LINEAR_OPTIONS,
} from '@components/table-input/tableInput.helpers.ars';

import { useLookupTablesQuery } from '@api/services/lookup/hooks';
import { ArType } from '@api/services/lookup/types';
import AquaticResourceService from '@src/api/services/aquatic-resources/service';
import { ResponseStatuses, statesData } from '@src/utils/enums';

import { useAquaticResources, useTableInput } from '@stores';

import _cloneDeep from 'lodash/cloneDeep';
import * as yup from 'yup';

export const AR_TABLE_QUESTION_ID = 'aquatic-resources-table';
const AR_TYPE_COLUMN_INDEX = 11;
const MEASUREMENT_TYPE_COLUMN_INDEX = 4;
const MEASUREMENT_UNIT_COLUMN_INDEX = 6;
const STATE_COLUMN_INDEX = 1;
const LATITUDE_COLUMN_INDEX = 2;
const LONGITUDE_COLUMN_INDEX = 3;

type AquaticResourceTableProps = {
  apiInvalidPageNumbers?: number[];
  apiValidationUpdatedAt?: number;
  aquaticResourcesData: AquaticResourceData[];
  determination: string;
  getBlankRow: () => TableInputRowData;
  isDataLoading: boolean;
  isReadOnly: boolean;
  onBeforeSort?: () => Promise<boolean>;
  onChange: (changeData: TableInputOnChange) => void;
  onRemove?: (tableId: TableId) => void;
  onValidationChange: (isValid: boolean) => void;
  requestId: number;
  requestState: string;
  savedRowCount: number;
  validationSchema?: ReturnType<typeof yup.object>;
};

const AquaticResourcesTable = ({
  apiInvalidPageNumbers,
  apiValidationUpdatedAt,
  aquaticResourcesData,
  getBlankRow,
  isDataLoading,
  isReadOnly,
  onBeforeSort,
  onChange,
  onRemove,
  onValidationChange,
  requestId,
  requestState,
  savedRowCount,
  validationSchema,
}: AquaticResourceTableProps) => {
  const arTableId = `${AR_TABLE_QUESTION_ID}-${requestId}`;
  const { data: lookupTableData } = useLookupTablesQuery();

  const {
    setTableInputCellError,
    getTableInputData,
    getTableInputDeletes,
    getTableInputPageNumber,
    getTableInputUpdates,
    setTableInputAnswer,
    setTableInputCellOptions,
    setTableInputColumnOptions,
  } = useTableInput();

  const aquaticResourceMismatchIds = useAquaticResources((state) => state.aquaticResourceMismatchIds);
  const aquaticResourceGeometryBoundaryFailureIds = useAquaticResources(
    (state) => state.aquaticResourceGeometryBoundaryFailureIds
  );
  const aquaticResourceDuplicateWaterNameIds = useAquaticResources(
    (state) => state.aquaticResourceDuplicateWaterNameIds
  );
  const removeAquaticResourceDuplicateWaterName = useAquaticResources(
    (state) => state.removeAquaticResourceDuplicateWaterName
  );
  const pageNumber = getTableInputPageNumber(arTableId);
  const [editedPageNumbers, setEditedPageNumbers] = useState<Set<number>>(() => new Set());
  const [locallyInvalidPageNumbers, setLocallyInvalidPageNumbers] = useState<number[]>([]);

  const answerData = useMemo(
    () => convertArApiDataToTableInputData(aquaticResourcesData, pageNumber),
    [aquaticResourcesData, pageNumber]
  );

  const columnData = useMemo(() => {
    const baseColumnData = _cloneDeep(arTableInputColumnData);

    if (lookupTableData?.Cowardins) {
      baseColumnData[8].options = lookupTableData.Cowardins.map((code, index) => ({
        label: code.shortCode as string,
        value: code.shortCode as string,
        sortOrder: index,
      }));
    }

    if (lookupTableData?.HGMCodes) {
      baseColumnData[9].options = lookupTableData.HGMCodes.map((code, index) => ({
        label: code.shortCode as string,
        value: code.shortCode as string,
        sortOrder: index,
      }));
    }

    return baseColumnData;
  }, [lookupTableData?.Cowardins, lookupTableData?.HGMCodes]);

  const handleValidateRow = useCallback(
    (rowData: TableInputRowData, allRows: TableInputRowData[]): TableInputCellValidation[] => {
      if (!validationSchema) {
        return [];
      }

      const aquaticResourceRow = buildAquaticResourceRow(rowData);
      const waterNameCounts = getWaterNameCounts(allRows); // builds the water-name count lookup once per validation pass, then reuses it for each row

      try {
        validationSchema.validateSync(aquaticResourceRow, {
          abortEarly: false,
          context: {
            aquaticResourceMismatchIds,
            aquaticResourceGeometryBoundaryFailureIds,
            aquaticResourceDuplicateWaterNameIds,
            updatedRowData: aquaticResourceRow,
            waterNameCounts,
          },
        });

        return [];
      } catch (error: unknown) {
        const validationErrors =
          typeof error === 'object' &&
          error !== null &&
          'inner' in error &&
          Array.isArray((error as { inner: unknown[] }).inner) &&
          (error as { inner: unknown[] }).inner.length > 0
            ? (error as { inner: unknown[] }).inner
            : [error];
        return validationErrors.reduce<TableInputCellValidation[]>((acc, validationError) => {
          const path = (
            validationError as {
              path?: string;
            }
          ).path;

          if (!path) {
            return acc;
          }
          const colIndex =
            path === 'jurisdictionalDeterminationType'
              ? AR_PROP_COL_IDX.jurisdictionalDeterminationType
              : path === 'aquaticResourceType'
                ? AR_PROP_COL_IDX.aquaticResourceType
                : AR_PROP_COL_IDX[path as keyof typeof AR_PROP_COL_IDX];

          if (colIndex === undefined) {
            return acc;
          }
          if (!acc.some((item) => item.colIndex === colIndex)) {
            acc.push({
              colIndex,
              message: (
                validationError as {
                  message: string;
                }
              ).message,
            });
          }

          return acc;
        }, []);
      }
    },
    [
      aquaticResourceDuplicateWaterNameIds,
      aquaticResourceGeometryBoundaryFailureIds,
      aquaticResourceMismatchIds,
      validationSchema,
    ]
  );

  const effectiveApiInvalidPageNumbers = useMemo(
    () =>
      (apiInvalidPageNumbers ?? []).filter((pageNumber) => {
        const wasEdited = editedPageNumbers.has(pageNumber);
        const isStillInvalidLocally = locallyInvalidPageNumbers.includes(pageNumber);
        return !wasEdited || isStillInvalidLocally;
      }),
    [apiInvalidPageNumbers, editedPageNumbers, locallyInvalidPageNumbers]
  );

  const getRegulationTypeForState = useCallback(
    (state: string) =>
      lookupTableData?.StateRegulations?.find((reg) => reg.state.toUpperCase() === state.toUpperCase())
        ?.regulationType ?? '',
    [lookupTableData?.StateRegulations]
  );

  const getArOptionsForState = useCallback(
    (state: string): TableInputSelectOption[] => {
      const stateRegulationType = lookupTableData?.StateRegulations?.find((reg) => reg.state === state)?.regulationType;
      const stateArTypeOptions = lookupTableData?.ARTypes?.reduce((acc: TableInputSelectOption[], type: ArType) => {
        if (type.regulationType === stateRegulationType) {
          acc.push({
            label: type.shortCode,
            value: type.shortCode,
          });
        }
        return acc;
      }, []);
      return stateArTypeOptions ?? [];
    },
    [lookupTableData?.ARTypes, lookupTableData?.StateRegulations]
  );

  const clearResolvedDuplicateWaterNames = useCallback(
    (action: 'update' | 'delete', recordId: number | string) => {
      const dataByPage = getTableInputData(arTableId)?.pagedData ?? {};
      const allRows = Object.values(dataByPage).flatMap(({ rowData }) => rowData);
      const commonParams = {
        allRows,
        tableDeletes: getTableInputDeletes(arTableId) ?? {},
        tableUpdates: getTableInputUpdates(arTableId) ?? {},
      };
      const idsToClear =
        action === 'update'
          ? getResolvedDuplicateWaterNameIdsAfterUpdate({
              ...commonParams,
              updatedRecordId: recordId,
            })
          : getResolvedDuplicateWaterNameIds({
              ...commonParams,
              deletedRecordId: recordId,
            });
      idsToClear.forEach((aquaticResourceId) => {
        removeAquaticResourceDuplicateWaterName([aquaticResourceId]);
      });
    },
    [arTableId, getTableInputData, getTableInputDeletes, getTableInputUpdates, removeAquaticResourceDuplicateWaterName]
  );

  const handleOnChange = useCallback(
    async (changeData: TableInputOnChange) => {
      const { action, colIndex, pageNumber, recordId, rowIndex = 0, value } = changeData;

      const shouldTrackPageEdit =
        action === 'update' || action === 'delete' || action === 'undo-delete' || action === 'add';

      if (shouldTrackPageEdit) {
        setEditedPageNumbers((currentPages) => {
          if (currentPages.has(pageNumber)) {
            return currentPages;
          }

          const nextPages = new Set(currentPages);
          nextPages.add(pageNumber);

          return nextPages;
        });
      }

      if (action === 'update' && colIndex === AR_PROP_COL_IDX.waterName && recordId != null) {
        clearResolvedDuplicateWaterNames('update', recordId);
      }

      if (action === 'delete' && recordId != null) {
        clearResolvedDuplicateWaterNames('delete', recordId);
      }

      const updateArTypeOptions = colIndex === STATE_COLUMN_INDEX && action === 'update';

      const updateMeasurementUnitOptions = colIndex === MEASUREMENT_TYPE_COLUMN_INDEX && action === 'update';

      const validateStateCoordinates =
        (colIndex === STATE_COLUMN_INDEX ||
          colIndex === LATITUDE_COLUMN_INDEX ||
          colIndex === LONGITUDE_COLUMN_INDEX) &&
        action === 'update';

      if (updateArTypeOptions) {
        const updatedState = value as string;

        const stateArTypeOptions = getArOptionsForState(updatedState);

        setTableInputCellOptions({
          tableId: arTableId,
          pageKey: pageNumber,
          rowIndex,
          colIndex: AR_TYPE_COLUMN_INDEX,
          options: stateArTypeOptions as TableInputSelectOption[],
        });

        if (recordId != null) {
          const regulationType = getRegulationTypeForState(updatedState);

          setTableInputAnswer(
            {
              action: 'update',
              colIndex: AR_PROP_COL_IDX.regulationType,
              recordId,
              value: regulationType,
            },
            arTableId
          );
        }
      } // Set measurement unit options based on measurement type.

      if (updateMeasurementUnitOptions) {
        setTableInputCellOptions({
          tableId: arTableId,
          pageKey: pageNumber,
          rowIndex,
          colIndex: MEASUREMENT_UNIT_COLUMN_INDEX,
          options: value === 'Area' ? MEASUREMENT_UNIT_AREA_OPTIONS : MEASUREMENT_UNIT_LINEAR_OPTIONS,
        });
      }

      // State Coordinate Mismatch Validation
      if (validateStateCoordinates) {
        // Retrieve rowData and updated rowData
        const tableInputData = getTableInputData(arTableId);
        const rowData = tableInputData?.pagedData?.[pageNumber]?.rowData?.[rowIndex];
        const recordId = rowData?.[0]?.recordId;

        if (recordId == null) {
          return;
        }

        const tableUpdates = getTableInputUpdates(arTableId);
        const rowUpdates = tableUpdates?.[recordId];

        // Identify state, lat, lon values whether existing or updated values
        const state = rowUpdates?.[STATE_COLUMN_INDEX] ?? rowData?.[STATE_COLUMN_INDEX]?.value;
        const stateAbbrev = statesData.filter((item) => item.state === state)?.[0].stateABBR;
        const latitude = rowUpdates?.[LATITUDE_COLUMN_INDEX] ?? rowData?.[LATITUDE_COLUMN_INDEX]?.value;
        const longitude = rowUpdates?.[LONGITUDE_COLUMN_INDEX] ?? rowData?.[LONGITUDE_COLUMN_INDEX]?.value;

        if (!stateAbbrev || !latitude || !longitude) {
          return;
        }

        // Run State Coordinate Mismatch Validation endpoint
        const response = await AquaticResourceService.getAquaticResourceMismatch({
          stateAbbrev,
          latitude: Number(latitude),
          longitude: Number(longitude),
        });

        // Trigger cell error when response status is "Validation Failed"
        const errorMessage =
          response?.status === ResponseStatuses.ValidationFailed
            ? 'The provided coordinates (latitude/longitude) do not reside within the selected state.'
            : undefined;
        setTableInputCellError({
          tableId: arTableId,
          pageKey: pageNumber,
          rowIndex,
          colIndex: STATE_COLUMN_INDEX,
          errorMessage: errorMessage,
        });
      }

      onChange(changeData);
    },
    [
      arTableId,
      clearResolvedDuplicateWaterNames,
      getArOptionsForState,
      getRegulationTypeForState,
      onChange,
      setTableInputAnswer,
      setTableInputCellOptions,
      getTableInputData,
      getTableInputUpdates,
      setTableInputCellError,
    ]
  );

  const handleInvalidLoadedPageNumbersChange = useCallback((nextPageNumbers: number[]) => {
    setLocallyInvalidPageNumbers((currentPageNumbers) => {
      const didPagesChange =
        currentPageNumbers.length !== nextPageNumbers.length ||
        currentPageNumbers.some((pageNumber, index) => pageNumber !== nextPageNumbers[index]);

      return didPagesChange ? nextPageNumbers : currentPageNumbers;
    });
  }, []);

  // set the default column AR Type options based on the request state
  useEffect(() => {
    const requestStateArTypeOptions = getArOptionsForState(requestState);

    setTableInputColumnOptions({
      tableId: arTableId,
      colIndex: AR_TYPE_COLUMN_INDEX,
      options: requestStateArTypeOptions as TableInputSelectOption[],
    });
  }, [arTableId, getArOptionsForState, requestState, setTableInputColumnOptions]);

  // fresh API validation response source of truth
  useEffect(() => {
    if (!apiValidationUpdatedAt) {
      return;
    }
    setEditedPageNumbers(new Set());
  }, [apiValidationUpdatedAt]);

  return (
    <div className='ar-table-input-container'>
      <TableInput
        answerData={answerData}
        columnData={columnData}
        getBlankRow={getBlankRow}
        isReadOnly={isReadOnly}
        isDataLoading={isDataLoading}
        invalidUnloadedPageNumbers={effectiveApiInvalidPageNumbers}
        onBeforeSort={onBeforeSort}
        onChange={handleOnChange}
        onInvalidLoadedPageNumbersChange={handleInvalidLoadedPageNumbersChange}
        onRemove={onRemove}
        onValidationChange={onValidationChange}
        showPageSizeSelector={false}
        startingTotalRowCount={savedRowCount}
        onValidateRow={handleValidateRow}
        tableId={arTableId}
      />
    </div>
  );
};

export default AquaticResourcesTable;
